import { freeV1 } from './free-v1';
import { freeV2 } from './free-v2';
import { freeV3 } from './free-v3';
import { analyticsV1 } from './analytics-v1';
import { replenishmentV1 } from './replenishment-v1';
import { replenishmentV2 } from './replenishment-v2';
import { replenishmentV3, analyticsV3 } from './replenishment-v3';
import { MenuVersion } from '@/config/menuRollout';

/**
 * Decide whether the store holds the Replenishment Center for real, from the
 * entitlement rules rather than the package name (AS-10794).
 *
 * Kept as a pure function, and out of VerticalMenuBar, so the initialisation
 * guard is testable: `hasPermission` answers `true` while
 * `$permissionsInitializing` is set, and `store.validateRule` answers valid
 * while `rules.items` is empty. Feeding either straight into the shape
 * decision would flip every paid merchant onto the converged menu for the
 * first frames and then snap back.
 *
 * While either is still true the override is inert, which preserves the
 * documented fail-safe below: an unresolved store shows the twins, never hides.
 */
export function resolveReplenishmentEntitlement(
  permissionsInitializing: boolean,
  ruleItemCount: number,
  hasBuyingTable: boolean,
): boolean {
  if (permissionsInitializing) { return false; }
  if (ruleItemCount === 0) { return false; }
  return hasBuyingTable;
}

export function selectMenuConfig(
  version: MenuVersion,
  isFree: boolean,
  packageName: string | undefined,
  hasReplenishmentEntitlement = false,
): any[] {
  if (version === 1) {
    if (isFree) { return freeV1.data; }
    if (packageName?.toLowerCase().includes('replenishment')) { return replenishmentV1.data; }
    return analyticsV1.data;
  }
  if (version === 3) {
    if (isFree) { return freeV3.data; }
    // Per-package split (Release A, spec 2026-08-05): only Replenishment
    // holders get the converged shape; other paid tiers keep the classic
    // twins (analyticsV3) — their only ACCESSIBLE forecasting entries.
    // Fail-safe: an unresolved package name shows the twins, never hides.
    // The package name is still checked first so a genuine Replenishment
    // merchant matches on the very first render, before permissions resolve.
    // The entitlement covers the merchant whose plan name says Professional
    // but whose rules say buying_table — unlocking features writes to
    // store.rules, never to currentPackage.name (AS-10794).
    if (packageName?.toLowerCase().includes('replenishment') || hasReplenishmentEntitlement) {
      return replenishmentV3.data;
    }
    return analyticsV3.data;
  }
  return isFree ? freeV2.data : replenishmentV2.data;
}

/**
 * Pure decision for whether/what version-switcher entry to append to the
 * sidebar menu. v3 users always see a way back to classic; v1 and v2 users
 * see a way to try v3 (v1 must not be a dead end — a merchant on the legacy
 * menu, via menuVersionChoice='1' or legacy menuVersion='1', needs a UI path
 * forward). The rollout is package-independent as of phase 2, so this no
 * longer gates on isReplenishmentPackage.
 *
 * Free users are the exception: they are locked to their analytics-first menu
 * (freeV3) and get no menu-style switcher at all — switching would expose paid
 * menu styles (e.g. the replenishment menu's AI Report Builder) they can't use.
 */
/**
 * AS-11272 M6: the sidebar entry for moving the store to the new app, shown
 * on every page. Appended after the version switcher, only when the bot said
 * this caller may (`store.frontendAppOptInAvailable`: owner + C3 gate open).
 * The sidebar is the one surface every merchant sees: `/settings/*` defaults
 * to the atlas-hosted settings, so an entry inside warbler's Settings.vue
 * alone would reach only merchants who chose classic settings. The click is
 * handled by VerticalMenuBar (`atlasOptIn`), never by a link.
 */
export const ATLAS_OPT_IN_SECTION = 'atlas-opt-in';

export function atlasOptInMenuSection(available: boolean): any | null {
  if (!available) {
    return null;
  }
  return {
    section: ATLAS_OPT_IN_SECTION,
    title: 'New version',
    icon: 'mdi-rocket-launch-outline',
    type: 'group',
    items: [{
      title: 'Try the new version',
      icon: 'mdi-rocket-launch-outline',
      type: 'single',
      atlasOptIn: true,
    }],
  };
}

export function versionSwitcherEntry(
  version: MenuVersion,
  isFree = false,
): { title: string; switchToVersion: 2 | 3 } | null {
  if (isFree) {
    return null;
  }
  if (version === 3) {
    return { title: 'Switch to classic menu', switchToVersion: 2 };
  }
  return { title: 'Try the new menu', switchToVersion: 3 };
}
